import { Routes } from '@angular/router';
import { catalogCapGuard } from './Modules/Shared/Guards/catalog-cap.guard';
import { guestGuard } from './Modules/Shared/Guards/guest.guard';
import { loggedInGuard } from './Modules/Shared/Guards/logged-in.guard';
import { notSelfGuard } from './Modules/Shared/Guards/not-self.guard';
import { moderatorCreateGuard } from './Modules/Shared/Guards/moderator-create.guard';
import { permissionGuard } from './Modules/Shared/Guards/permission.guard';
import { BillingFacade } from './Facades/billing.facade';
import { BillingInvoicesFacade } from './Facades/billing-invoices.facade';
import { GeneralSettingsFacade } from './Facades/general-settings.facade';
import { UserFormFacade } from './Facades/user-form.facade';
import { ApplicationDetailFacade } from './Modules/Catalog/Facades/application-detail.facade';
import { GroupDetailFacade } from './Modules/Catalog/Facades/group-detail.facade';
import { MyApprovalsFacade } from './Modules/Catalog/Facades/my-approvals.facade';
import { MyApprovalDetailFacade } from './Modules/Catalog/Facades/my-approval-detail.facade';
import { MyDemandsFacade } from './Modules/Catalog/Facades/my-demands.facade';
import { MyDemandDetailFacade } from './Modules/Catalog/Facades/my-demand-detail.facade';
import { Permissions } from './Enums/permission.enum';
import { settingsLandingGuard } from './Modules/Shared/Guards/settings-landing.guard';
import {
  ADMINISTRATION_ROUTE_SEGMENT,
  BILLING_ROUTE_SEGMENT,
  GENERAL_ROUTE_SEGMENT,
  PLAN_ROUTE_SEGMENT,
  SUBSCRIPTION_ROUTE_SEGMENT,
  USERS_ROUTE_SEGMENT,
} from './Modules/Shared/Consts/billing-route.const';
import {
  PROFILE_DELEGATION_ROUTE_SEGMENT,
  PROFILE_GENERAL_ROUTE_SEGMENT,
  PROFILE_ROUTE_SEGMENT,
} from './Modules/Shared/Consts/profile-route.const';
import {
  DEMAND_REFERENCE_MISS_ROUTE_SEGMENT,
  DEMAND_REFERENCE_PARAM,
  DEMAND_REFERENCE_ROUTE_SEGMENT,
} from './Modules/Catalog/Consts/demand-reference-route.const';
import { DemandReferenceComponent } from './Modules/Catalog/Components/Pages/demand-reference/demand-reference.component';
import { DemandReferenceMissComponent } from './Modules/Catalog/Components/Pages/demand-reference-miss/demand-reference-miss.component';
import { REPORT_ROUTE_PERMISSIONS } from './Modules/Reports/Demand/Consts/all-demands-display.const';
import { AllDemandsFacade } from './Modules/Reports/Demand/Facades/all-demands.facade';
import { AllDemandDetailFacade } from './Modules/Reports/Demand/Facades/all-demand-detail.facade';
import { AllDemandsComponent } from './Modules/Reports/Demand/Components/Pages/all-demands/all-demands.component';
import { AllDemandDetailComponent } from './Modules/Reports/Demand/Components/Pages/all-demand-detail/all-demand-detail.component';
import { MonitoringFacade } from './Modules/Reports/Monitoring/Facades/monitoring.facade';
import { MonitoringComponent } from './Modules/Reports/Monitoring/Components/Pages/monitoring/monitoring.component';

export const routes: Routes = [
  {
    path: 'auth',
    loadComponent: () => import('./Modules/Auth/auth/auth.component').then((m) => m.AuthComponent),
    children: [
      {
        path: 'login',
        canActivate: [guestGuard],
        loadComponent: () => import('./Modules/Auth/Components/login/login.component').then((m) => m.LoginComponent),
        title: 'shared.routes.app.login',
      },
      { path: 'callback', loadComponent: () => import('./Modules/Auth/Components/callback/callback.component').then((m) => m.CallbackComponent), title: 'shared.routes.app.callback' },
      { path: 'impersonation-expired', loadComponent: () => import('./Modules/Auth/Components/impersonation-expired/impersonation-expired.component').then((m) => m.ImpersonationExpiredComponent), title: 'shared.routes.app.impersonation_expired' },
      { path: '', redirectTo: 'login', pathMatch: 'full' },
      { path: '**', redirectTo: 'login', pathMatch: 'full' },
    ],
  },
  {
    path: 'settings',
    canActivate: [loggedInGuard, catalogCapGuard],
    loadComponent: () => import('./Modules/Settings/settings/settings.component').then((m) => m.SettingsComponent),
    title: 'shared.routes.app.settings',
    children: [
      {
        path: USERS_ROUTE_SEGMENT,
        loadComponent: () => import('./Modules/User-management/user-management/user-management.component').then((m) => m.UserManagementComponent),
        canActivate: [permissionGuard],
        data: { permission: Permissions.USER_MANAGER },
        children: [
          {
            path: '',
            loadComponent: () => import('./Modules/User-management/Components/Pages/users-dashboard/users-dashboard.component').then((m) => m.UsersDashboardComponent),
            title: 'shared.routes.app.settings_users',
          },
          {
            path: 'add',
            loadComponent: () => import('./Modules/User-management/Components/Pages/user-create/user-create.component').then((m) => m.UserCreateComponent),
            providers: [UserFormFacade],
            title: 'shared.routes.app.settings_user_add',
          },
          {
            path: 'edit/:id',
            loadComponent: () => import('./Modules/User-management/Components/Pages/user-edit/user-edit.component').then((m) => m.UserEditComponent),
            canActivate: [notSelfGuard],
            providers: [UserFormFacade],
            title: 'shared.routes.app.settings_user_edit',
          },
          { path: '**', redirectTo: '', pathMatch: 'full' },
        ],
      },
      {
        path: PLAN_ROUTE_SEGMENT,
        loadComponent: () => import('./Modules/Settings/Components/Pages/plan/plan.component').then((m) => m.SettingsPlanComponent),
        canActivate: [permissionGuard],
        data: { permission: Permissions.BILLING_MANAGER },
        providers: [BillingFacade],
        title: 'shared.routes.app.settings_plan',
      },
      {
        path: SUBSCRIPTION_ROUTE_SEGMENT,
        loadComponent: () => import('./Modules/Settings/Components/Pages/subscription/subscription.component').then((m) => m.SettingsSubscriptionComponent),
        canActivate: [permissionGuard],
        data: { permission: Permissions.BILLING_MANAGER },
        providers: [BillingFacade],
        title: 'shared.routes.app.settings_subscription',
      },
      {
        path: BILLING_ROUTE_SEGMENT,
        loadComponent: () => import('./Modules/Settings/Components/Pages/billing-payment/billing-payment.component').then((m) => m.SettingsBillingPaymentComponent),
        canActivate: [permissionGuard],
        data: { permission: Permissions.BILLING_MANAGER },
        providers: [BillingFacade, BillingInvoicesFacade],
        title: 'shared.routes.app.settings_billing',
      },
      {
        path: GENERAL_ROUTE_SEGMENT,
        loadComponent: () => import('./Modules/Settings/Components/Pages/general/general.component').then((m) => m.SettingsGeneralComponent),
        canActivate: [permissionGuard],
        data: { permission: Permissions.GENERAL_SETTINGS_MANAGER },
        providers: [GeneralSettingsFacade],
        title: 'shared.routes.app.settings_general',
      },
      {
        path: ADMINISTRATION_ROUTE_SEGMENT,
        loadComponent: () => import('./Modules/Settings/Components/Pages/administration/administration.component').then((m) => m.SettingsAdministrationComponent),
        canActivate: [permissionGuard],
        data: { permission: Permissions.BILLING_MANAGER },
        title: 'shared.routes.app.settings_administration',
      },
      { path: '', pathMatch: 'full', canActivate: [settingsLandingGuard], children: [] },
      { path: '**', canActivate: [settingsLandingGuard], children: [] },
    ],
  },
  {
    path: PROFILE_ROUTE_SEGMENT,
    canActivate: [loggedInGuard, catalogCapGuard],
    loadComponent: () => import('./Modules/Profile/profile/profile.component').then((m) => m.ProfileComponent),
    title: 'shared.routes.app.profile',
    children: [
      {
        path: PROFILE_GENERAL_ROUTE_SEGMENT,
        loadComponent: () => import('./Modules/Profile/Components/Pages/general/general.component').then((m) => m.ProfileGeneralComponent),
        title: 'shared.routes.app.profile_general',
      },
      {
        path: PROFILE_DELEGATION_ROUTE_SEGMENT,
        loadComponent: () => import('./Modules/Profile/Components/Pages/delegation/delegation.component').then((m) => m.ProfileDelegationComponent),
        title: 'shared.routes.app.profile_delegation',
      },
      { path: '', redirectTo: PROFILE_GENERAL_ROUTE_SEGMENT, pathMatch: 'full' },
      { path: '**', redirectTo: PROFILE_GENERAL_ROUTE_SEGMENT, pathMatch: 'full' },
    ],
  },
  {
    path: 'blocked',
    canActivate: [loggedInGuard],
    loadComponent: () => import('./Modules/Catalog/Components/Pages/blocked/blocked.component').then((m) => m.BlockedComponent),
    title: 'shared.routes.app.blocked',
  },
  {
    path: '',
    canActivate: [loggedInGuard, catalogCapGuard],
    loadComponent: () => import('./Modules/Catalog/catalog/catalog.component').then((m) => m.CatalogComponent),
    title: 'shared.routes.app.catalog',
    children: [
      { path: 'applications', loadComponent: () => import('./Modules/Catalog/Components/Pages/applications/applications.component').then((m) => m.ApplicationsComponent), title: 'shared.routes.app.applications' },
      {
        path: 'applications/:id',
        loadComponent: () => import('./Modules/Catalog/Components/Pages/application-detail/application-detail.component').then((m) => m.ApplicationDetailComponent),
        providers: [ApplicationDetailFacade],
        title: 'shared.routes.app.applicationDetail',
        data: { breadcrumb: [{ labelKey: 'shared.routes.app.applications', link: '/applications' }] },
      },
      {
        path: 'groups',
        loadComponent: () => import('./Modules/Catalog/Components/Pages/groups/groups.component').then((m) => m.GroupsComponent),
        title: 'shared.routes.app.groups',
      },
      {
        path: 'groups/:id',
        loadComponent: () => import('./Modules/Catalog/Components/Pages/group-detail/group-detail.component').then((m) => m.GroupDetailComponent),
        providers: [GroupDetailFacade],
        title: 'shared.routes.app.groupDetail',
        data: { breadcrumb: [{ labelKey: 'shared.routes.app.groups', link: '/groups' }] },
      },
      {
        path: 'my-approvals',
        loadComponent: () => import('./Modules/Catalog/Components/Pages/my-approvals/my-approvals.component').then((m) => m.MyApprovalsComponent),
        providers: [MyApprovalsFacade],
        title: 'shared.routes.app.myApprovals',
      },
      {
        path: 'my-approvals/:type/:id',
        loadComponent: () => import('./Modules/Catalog/Components/Pages/my-approval-detail/my-approval-detail.component').then((m) => m.MyApprovalDetailComponent),
        providers: [MyApprovalDetailFacade],
        title: 'shared.routes.app.myApprovalDetail',
        data: { breadcrumb: [{ labelKey: 'shared.routes.app.myApprovals', link: '/my-approvals' }] },
      },
      {
        path: 'my-demands',
        loadComponent: () => import('./Modules/Catalog/Components/Pages/my-demands/my-demands.component').then((m) => m.MyDemandsComponent),
        providers: [MyDemandsFacade],
        title: 'shared.routes.app.myDemands',
      },
      {
        path: 'my-demands/:type/:id',
        loadComponent: () => import('./Modules/Catalog/Components/Pages/my-demand-detail/my-demand-detail.component').then((m) => m.MyDemandDetailComponent),
        providers: [MyDemandDetailFacade],
        title: 'shared.routes.app.myDemandDetail',
        data: { breadcrumb: [{ labelKey: 'shared.routes.app.myDemands', link: '/my-demands' }] },
      },
      {
        path: `${DEMAND_REFERENCE_ROUTE_SEGMENT}/${DEMAND_REFERENCE_MISS_ROUTE_SEGMENT}`,
        component: DemandReferenceMissComponent,
        title: 'shared.routes.app.demandReferenceMiss',
        data: { breadcrumb: [{ labelKey: 'shared.routes.app.myDemands', link: '/my-demands' }] },
      },
      {
        path: `${DEMAND_REFERENCE_ROUTE_SEGMENT}/:${DEMAND_REFERENCE_PARAM}`,
        component: DemandReferenceComponent,
        title: 'shared.routes.app.demandReference',
        data: { breadcrumb: [{ labelKey: 'shared.routes.app.myDemands', link: '/my-demands' }] },
      },
      {
        path: 'manage/applications',
        loadComponent: () => import('./Modules/Application-management/application-management/application-management.component').then((m) => m.ApplicationManagementComponent),
        canActivate: [permissionGuard],
        data: {
          permission: [
            Permissions.APPLICATION_MANAGER,
            Permissions.APPLICATION_MODERATOR,
          ],
        },
        title: 'shared.routes.app.settings_applications',
        children: [
          {
            path: '',
            loadComponent: () => import('./Modules/Application-management/Components/Pages/application-dashboard/application-dashboard.component').then((m) => m.ApplicationDashboardComponent),
            title: 'shared.routes.app.settings_applications_list',
          },
          {
            path: 'add',
            loadComponent: () => import('./Modules/Application-management/Components/Pages/application-form/application-form.component').then((m) => m.ApplicationFormComponent),
            canActivate: [moderatorCreateGuard],
            data: {
              settingKey: 'moderator_can_create_applications',
              bypassPermission: Permissions.APPLICATION_MANAGER,
            },
            title: 'shared.routes.app.settings_application_form',
          },
          {
            path: 'edit/:id',
            loadComponent: () => import('./Modules/Application-management/Components/Pages/application-form/application-form.component').then((m) => m.ApplicationFormComponent),
            title: 'shared.routes.app.settings_application_form',
          },
          { path: '**', redirectTo: '', pathMatch: 'full' },
        ],
      },
      {
        path: 'manage/groups',
        loadComponent: () => import('./Modules/Group-management/group-management/group-management.component').then((m) => m.GroupManagementComponent),
        canActivate: [permissionGuard],
        data: { permission: [Permissions.GROUP_MANAGER, Permissions.GROUP_MODERATOR] },
        title: 'shared.routes.app.settings_groups',
        children: [
          { path: '', loadComponent: () => import('./Modules/Group-management/Components/Pages/group-dashboard/group-dashboard.component').then((m) => m.GroupDashboardComponent), title: 'shared.routes.app.settings_groups_list' },
          {
            path: 'add',
            loadComponent: () => import('./Modules/Group-management/Components/Pages/group-form/group-form.component').then((m) => m.GroupFormComponent),
            canActivate: [moderatorCreateGuard],
            data: {
              settingKey: 'moderator_can_create_groups',
              bypassPermission: Permissions.GROUP_MANAGER,
            },
            title: 'shared.routes.app.settings_group_form',
          },
          { path: 'edit/:id', loadComponent: () => import('./Modules/Group-management/Components/Pages/group-form/group-form.component').then((m) => m.GroupFormComponent), title: 'shared.routes.app.settings_group_form' },
          { path: '**', redirectTo: '', pathMatch: 'full' },
        ],
      },
      {
        path: 'reports/all-demands',
        component: AllDemandsComponent,
        canActivate: [loggedInGuard, catalogCapGuard, permissionGuard],
        data: { permission: REPORT_ROUTE_PERMISSIONS },
        providers: [AllDemandsFacade],
        title: 'shared.routes.app.reportsAllDemands',
      },
      {
        path: 'reports/all-demands/:type/:id',
        component: AllDemandDetailComponent,
        canActivate: [loggedInGuard, catalogCapGuard, permissionGuard],
        data: {
          permission: REPORT_ROUTE_PERMISSIONS,
          breadcrumb: [{ labelKey: 'shared.routes.app.reportsAllDemands', link: '/reports/all-demands' }],
        },
        providers: [AllDemandDetailFacade],
        title: 'shared.routes.app.reportsAllDemandDetail',
      },
      {
        path: 'reports/monitoring',
        component: MonitoringComponent,
        canActivate: [loggedInGuard, catalogCapGuard, permissionGuard],
        data: { permission: [Permissions.REPORT_MANAGER] },
        providers: [MonitoringFacade],
        title: 'shared.routes.app.reportsMonitoring',
      },
      { path: '**', redirectTo: 'applications', pathMatch: 'full' },
    ],
  },
  { path: '**', redirectTo: '', pathMatch: 'full' },
];
